/**
 * Sculpted stone balusters, molded piers and their copings for a curved stone
 * span (lifted from lantern-water's `bridge-balustrade.js`). Eight-sided turned
 * spindles keep a carved silhouette at the walking camera's scale and share the
 * kit's limestone. The spindle geometry is the kit's (`bridge:spindle`).
 */
import * as THREE from 'three';
import type { Ctx } from './kit.ts';

const profile: readonly (readonly [number, number])[] = [
  [0, 0],
  [0.115, 0],
  [0.115, 0.055],
  [0.079, 0.09],
  [0.053, 0.17],
  [0.083, 0.25],
  [0.103, 0.34],
  [0.086, 0.42],
  [0.045, 0.51],
  [0.059, 0.59],
  [0.1, 0.615],
  [0.1, 0.67],
  [0, 0.67],
];

const spindleGeometry = (): THREE.LatheGeometry => {
  const spindle = new THREE.LatheGeometry(
    profile.map(([r, y]) => new THREE.Vector2(r, y)),
    8,
  );
  const uv = spindle.getAttribute('uv');
  for (let i = 0; i < uv.count; i += 1) uv.setXY(i, (uv.getX(i) * 0.72) / 2.5, (uv.getY(i) * 0.67) / 2.5);
  return spindle;
};

/** The balustrades of both sides of a span from `start` to `end` (x) at `z`, standing on `height(x)`. */
export const bridgeBalustrade = (ctx: Ctx, parent: THREE.Object3D, start: number, end: number, z: number, height: (x: number) => number): THREE.Group => {
  const { box, mesh, m } = ctx;
  const spindle = ctx.kit.shared('bridge:spindle', spindleGeometry);
  const group = new THREE.Group();
  group.name = 'carved-stone-balustrade';
  parent.add(group);
  const piers = [0.025, 0.25, 0.5, 0.75, 0.975].map((t) => THREE.MathUtils.lerp(start, end, t));
  for (const side of [-1, 1]) {
    const zz = z + side * 1.72;
    for (let bay = 0; bay < piers.length - 1; bay += 1) {
      for (let n = 1; n <= 3; n += 1) {
        const x = THREE.MathUtils.lerp(piers[bay] ?? start, piers[bay + 1] ?? end, n / 4);
        const y = height(x);
        mesh(group, spindle, m.stone, x, y + 0.24, zz);
        box(group, x, y + 0.26, zz, 0.26, 0.065, 0.28, m.stone);
        box(group, x, y + 0.9, zz, 0.25, 0.075, 0.29, m.limestone);
      }
    }
    piers.forEach((x, index) => {
      const y = height(x);
      box(group, x, y + 0.57, zz, 0.32, 1.13, 0.42, m.stone);
      box(group, x, y + 0.21, zz, 0.4, 0.15, 0.49, m.stone);
      box(group, x, y + 1.08, zz, 0.39, 0.08, 0.48, m.limestone);
      box(group, x, y + 1.16, zz, 0.48, 0.1, 0.55, m.cream);
      box(group, x, y + 1.225, zz, 0.38, 0.035, 0.45, m.limestone);
      // A small raised lozenge on the middle pier gives each span a focal mark.
      if (index === 2) {
        for (const face of [-1, 1]) {
          box(group, x, y + 0.63, zz + face * 0.218, 0.23, 0.43, 0.025, m.limestone);
          const diamond = box(group, x, y + 0.63, zz + face * 0.241, 0.125, 0.125, 0.027, m.cream);
          diamond.rotation.z = Math.PI / 4;
          diamond.scale.y *= 1.45;
        }
      }
    });
  }
  return group;
};
